import React from 'react';
import { IStore } from "../../../redux/reducers";
import { useSelector } from "react-redux";
import { StyleSheet, View } from 'react-native';
import { IUserJobListItem, JobStatus } from '../../../models';
import { Badge, Menu, Pressable, Box, Checkbox, useColorModeValue, Text, Progress } from 'native-base';

import { getDisplayableJobId, getBadgeColor, getStatusMessage, getFormattedDate } from '../../../utils/jobUtils'
import { listStyle } from 'src/styles/listStyle';
import { cardStyle } from 'src/styles/cardStyle';
import TextWithIcon from '../../TextWithIcon';
import MaterialIconByName from '../../MaterialIconByName';
import { getText } from 'src/utils/i18n';


interface UserJobHistoryListItemProps {
    item: IUserJobListItem;
    itemCallback: (item: IUserJobListItem) => void;
    downloadImageCallback: (jobId: string) => void;
    retryJobCallback: (item: IUserJobListItem) => void;
    deleteJobCallback: (jobId: string) => void;
    localProgressPercent?: number;
    localProgressMessage?: string;
    // Averaging feature props
    isSelectionMode?: boolean;
    isSelected?: boolean;
    onToggleSelection?: (jobId: string) => void;
    isAveraged?: boolean;
    localProfileName?: string;
}

export default function UserJobHistoryListItem(props: UserJobHistoryListItemProps) {
    const grainTypes = useSelector((store: IStore) => store.user.userProfile.response?.grains);
    const currentStack = useSelector((store: IStore) => store.app.currentStack);
    const isOnline = useSelector((store: IStore) => store.app.isOnline);
    const textColor = useColorModeValue('coolGray.800', 'coolGray.100');
    const cardBgColor = useColorModeValue('coolGray.100', 'coolGray.800');
    const selectedCardBgColor = useColorModeValue('primary.50', 'primary.800');
    const averagingCardBgColor = useColorModeValue('purple.50', 'purple.900');
    const borderColor = useColorModeValue('coolGray.300', 'coolGray.600');

    // Check if this is an averaging job
    const isAveragingJobItem = props.item.jobId.startsWith('avg-');

    const getGrainWithProfileName = (grainId: string, profileId?: string): string => {
        const grainName = grainTypes?.filter(grain => grain.grainId === grainId)[0]?.name || "";
        
        if (!profileId) {
            return grainName;
        }
        
        // Find the grain that matches the grainId
        const grain = grainTypes?.find(g => g.grainId === grainId);
        
        // Find the profile within that grain's profiles array
        const profile = grain?.profiles?.find(p => p.profileId === profileId);
        const profileName = profile?.profileName || props.localProfileName;
        
        if (profileName) {
            return `${grainName} (${profileName})`;
        }
        
        return grainName;
    }

    const getBackgroundColor = () => {
        if (isAveragingJobItem) return averagingCardBgColor;
        if (props.isSelected) return selectedCardBgColor;
        return cardBgColor;
    };

    const getBorderColorValue = () => {
        if (isAveragingJobItem) return "purple.400";
        if (props.isSelected) return "primary.500";
        return borderColor;
    };

    const isLocalJob = props.item.processingMode === 'local';
    const requiresInternet = !isOnline && !isLocalJob;
    const canDownloadImage = isLocalJob || isOnline;
    const localSyncStatus = isLocalJob ? props.item.syncStatus : undefined;
    const localSyncError = isLocalJob ? props.item.syncError : undefined;
    const localJobSynced = isLocalJob && (localSyncStatus === 'synced' || !!props.item.syncedAt);
    const showDelete = isLocalJob ? localJobSynced : true;
    const canDelete = isLocalJob ? localJobSynced : isOnline;
    const showLocalSyncIndicator = isLocalJob && localSyncStatus && localSyncStatus !== 'synced';
    const localSyncBadge = showLocalSyncIndicator
        ? (() => {
            const status = String(localSyncStatus || '').toLowerCase();
            if (status === 'syncing') return { label: 'SYNCING', color: 'orange' };
            if (status === 'failed') return { label: 'SYNC FAILED', color: 'red' };
            return { label: 'PENDING SYNC', color: 'gray' };
        })()
        : null;
    const showLocalProgress =
        isLocalJob
        && (props.item.jobStatus === JobStatus.JOB_IN_PROGRESS || props.item.jobStatus === JobStatus.JOB_PROCESSING_LOCAL)
        && typeof props.localProgressPercent === 'number';
    const localProgressLabel = showLocalProgress
        ? (() => {
            const pct = Math.max(0, Math.min(99, Math.round(props.localProgressPercent as number)));
            const msg = (props.localProgressMessage || '').trim();
            if (msg) {
                return `${msg} — ${pct}%`;
            }
            return `${pct}%`;
        })()
        : undefined;
    const showSelectionCheckbox = props.isSelectionMode && !isAveragingJobItem;

    return (
        <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Job ${getDisplayableJobId(props.item.jobId)}`}
            onPress={() => {
                // Don't allow selection of averaging jobs
                if (props.isSelectionMode && !isAveragingJobItem) {
                    props.onToggleSelection?.(props.item.jobId);
                } else if (!props.isSelectionMode) {
                    props.itemCallback(props.item);
                }
            }}
            style={cardStyle.normalCard}
            rounded="8"
            overflow="hidden"
            borderWidth="1"
            borderColor={getBorderColorValue()}
            shadow="3"
            bg={getBackgroundColor()}
            p="4"
            opacity={props.isSelectionMode && isAveragingJobItem ? 0.6 : 1}>
            <Box>
                <View style={[listStyle.cardSection, styles.cardMain]}>
                    <View style={styles.contentColumn}>
                        <View style={styles.jobHeader}>
                            <Text color={textColor} style={listStyle.title}>Job ID: {getDisplayableJobId(props.item.jobId)}</Text>
                            {props.isAveraged && (
                                <Badge colorScheme="info" variant="solid" ml={2} size="sm">
                                    {getText('average')}
                                </Badge>
                            )}
                            {props.item.processingMode && (
                                <Badge
                                    colorScheme={props.item.processingMode === 'local' ? "primary" : "secondary"}
                                    variant="subtle"
                                    ml={2}
                                    size="sm"
                                >
                                    {props.item.processingMode === 'local' ? getText('processingModeLocal') : getText('processingModeCloud')}
                                </Badge>
                            )}
                        </View>
                        <View style={styles.metadataStack}>
                            <TextWithIcon icon="barley" text={getGrainWithProfileName(props.item.grainId, props.item.profileId)} numberOfLines={1} />
                            <TextWithIcon icon="clock-time-nine" text={getFormattedDate(props.item.updatedAt ?? props.item.createdAt)} numberOfLines={1} />
                            <TextWithIcon icon="seed" text={
                                `${getText('variety')}: ${props.item.variety && props.item.variety !== "Default"
                                    ? props.item.variety
                                    : getText('notAvailable')}`
                            } numberOfLines={1} />
                        </View>
                    </View>
                    <View style={[styles.actionRail, !showSelectionCheckbox && styles.actionRailCentered]}>
                        {showSelectionCheckbox && (
                            <Box pointerEvents="none" mb={3}>
                                <Checkbox
                                    key={`checkbox-${props.item.jobId}`}
                                    value={props.item.jobId}
                                    isChecked={props.isSelected ?? false}
                                    accessibilityLabel={`Select job ${props.item.jobId}`}
                                />
                            </Box>
                        )}
                        <View style={styles.statusControls}>
                            {currentStack === "mlAdmin" &&
                                props.item.jobStatus === JobStatus.JOB_COMPLETED &&
                                props.item.mlLabelingStatus === "USED_FOR_LABELING" && (
                                    <Badge
                                        variant='solid'
                                        colorScheme="purple"
                                        style={{ marginRight: 8 }}
                                    >
                                        LABELED
                                    </Badge>
                                )
                            }
                            <Badge
                                variant='solid'
                                colorScheme={getBadgeColor(props.item.jobStatus)}
                                minW={92}
                                px={3}
                                alignItems="center"
                                justifyContent="center">
                                {getStatusMessage(props.item.jobStatus)}
                            </Badge>
                            <View style={styles.menuTrigger}>
                                <Menu trigger={triggerProps => {
                                    return <Pressable accessibilityLabel="More options" {...triggerProps}>
                                        <MaterialIconByName name="dots-vertical" />
                                    </Pressable>;
                                }}>
                                    {
                                        <Menu.Item isDisabled={requiresInternet} onPress={() => {
                                            props.retryJobCallback(props.item)
                                        }}>{getText('retry')}</Menu.Item>
                                    }
                                    <Menu.Item isDisabled={!canDownloadImage} onPress={() => {
                                        props.downloadImageCallback(props.item.jobId)
                                    }}>{getText('downloadImage')}</Menu.Item>
                                    {showDelete && (
                                        <Menu.Item isDisabled={!canDelete} onPress={() => {
                                            props.deleteJobCallback(props.item.jobId)
                                        }}>{getText('delete')}</Menu.Item>
                                    )}
                                </Menu>
                            </View>
                        </View>
                    </View>
                </View>
                {showLocalProgress && (
                    <Box mt={3}>
                        {!!localProgressLabel && (
                            <Text fontSize="xs" color="coolGray.500" mb={1}>
                                {localProgressLabel}
                            </Text>
                        )}
                        <Progress value={props.localProgressPercent} size="xs" colorScheme="orange" />
                    </Box>
                )}
                {showLocalSyncIndicator && localSyncBadge && (
                    <Box mt={3}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Badge variant="subtle" colorScheme={localSyncBadge.color} mr={2}>
                                {localSyncBadge.label}
                            </Badge>
                            {String(localSyncStatus || '').toLowerCase() === 'failed' && !!localSyncError && (
                                <Text fontSize="xs" color="coolGray.500" numberOfLines={2}>
                                    {String(localSyncError)}
                                </Text>
                            )}
                        </View>
                    </Box>
                )}
            </Box>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    cardMain: {
        alignItems: 'flex-start',
    },
    contentColumn: {
        flex: 1,
        minWidth: 0,
        paddingRight: 12,
    },
    jobHeader: {
        alignItems: 'flex-start',
        flexDirection: 'row',
        flexWrap: 'wrap',
        rowGap: 6,
    },
    metadataStack: {
        flexDirection: 'column',
        gap: 9,
        marginTop: 12,
        minWidth: 0,
    },
    actionRail: {
        alignItems: 'flex-end',
        flexShrink: 0,
        justifyContent: 'flex-start',
        marginLeft: 12,
        minWidth: 136,
    },
    actionRailCentered: {
        alignSelf: 'stretch',
        justifyContent: 'center',
    },
    statusControls: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    menuTrigger: {
        justifyContent: 'center',
        marginLeft: 8,
    },
});
